import React, { useState, useRef } from 'react';
import styles from './FileUploadModal.module.css';
import { IoClose, IoCloudUpload } from 'react-icons/io5';

const FileUploadModal = ({ isOpen, onClose, onSubmit, embedded = false }) => {
  const [uploadedImages, setUploadedImages] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  // Handle file selection
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || e.dataTransfer.files);
    
    // Filter for only image files
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length === 0) return;
    
    // Process each file
    imageFiles.forEach(processFile);
  };

  // Process a single file
  const processFile = async (file) => {
    // Show loading progress
    setUploadProgress(50);
    
    try {
      // Convert to base64
      const base64 = await readFileAsDataURL(file);
      
      // Add to state
      setUploadedImages(prev => [...prev, base64]);
      
      // Complete progress
      setUploadProgress(100);
      setTimeout(() => setUploadProgress(0), 500);
    } catch (error) {
      console.error('Error processing file:', error);
      setUploadProgress(0);
    }
  };

  // Convert file to base64
  const readFileAsDataURL = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        resolve(e.target.result);
      };
      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };
      reader.readAsDataURL(file);
    });
  };

  // Handle drag events
  const handleDragOver = (e) => {
    e.preventDefault();
    if (dropZoneRef.current) {
      dropZoneRef.current.classList.add(styles.dragOver);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    if (dropZoneRef.current) {
      dropZoneRef.current.classList.remove(styles.dragOver);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (dropZoneRef.current) {
      dropZoneRef.current.classList.remove(styles.dragOver);
    }
    handleFileSelect(e);
  };

  // Handle removing an image
  const handleRemoveImage = (index) => {
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
  };

  // Handle submit
  const handleSubmit = () => {
    if (uploadedImages.length > 0) {
      onSubmit(uploadedImages);
    }
    // Clear state
    setUploadedImages([]);
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (!isOpen) return null;

  // Content that will be rendered in both modes
  const content = (
    <>
      {!embedded && (
        <button className={styles.closeButton} onClick={onClose}>
          <IoClose size={24} />
        </button>
      )}
      
      {!embedded && <h3>Upload Images</h3>}
      
      <div 
        className={styles.dropZone} 
        ref={dropZoneRef}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input 
          type="file" 
          accept="image/*" 
          multiple 
          hidden 
          ref={fileInputRef}
          onChange={handleFileSelect}
        />
        <IoCloudUpload size={40} />
        <p>Click to browse or drop image files here</p>
        
        {uploadProgress > 0 && (
          <div className={styles.progressContainer}>
            <div 
              className={styles.progressBar}
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        )}
      </div>
      
      {uploadedImages.length > 0 && (
        <div className={styles.previewContainer}>
          {uploadedImages.map((src, index) => (
            <div key={index} className={styles.previewItem}>
              <img src={src} alt={`Preview ${index + 1}`} />
              <button
                className={styles.removeButton}
                onClick={() => handleRemoveImage(index)}
                title="Remove image"
              >
                <IoClose size={18} />
              </button>
            </div>
          ))}
        </div>
      )}
      
      <div className={styles.actionButtons}>
        {!embedded && (
          <button className={styles.cancelButton} onClick={onClose}>Cancel</button>
        )}
        <button 
          className={styles.submitButton} 
          onClick={handleSubmit}
          disabled={uploadedImages.length === 0}
        >
          Add Images
        </button>
      </div>
    </>
  );

  // If embedded, return content only
  if (embedded) {
    return content;
  }

  // Otherwise return modal wrapper
  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>
        {content}
      </div>
    </div>
  );
};

export default FileUploadModal;